import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  BACKFIRE_STORAGE_KEY,
  BackfireMode,
  BackfireSettings,
  DEFAULT_BACKFIRE_SETTINGS,
} from "@/constants/backfire";
import {
  normalizeBackfireSettings,
  parseStoredBackfireSettings,
} from "@/helper/backfire";
import { setBackfire } from "@/helper/contractFunctions/MafiaKillInitiator";

export interface BackfireState {
  settings: BackfireSettings;
  draftMode: BackfireMode;
  draftRangeMin: string;
  draftRangeMax: string;
  isSaving: boolean;
  hasLoaded: boolean;
}

const loadInitialSettings = (): BackfireSettings => {
  if (typeof window === "undefined") {
    return { ...DEFAULT_BACKFIRE_SETTINGS };
  }

  return parseStoredBackfireSettings(
    window.localStorage.getItem(BACKFIRE_STORAGE_KEY)
  );
};

const initialSettings = loadInitialSettings();

const initialState: BackfireState = {
  settings: initialSettings,
  draftMode: initialSettings.mode,
  draftRangeMin: String(initialSettings.rangeMin),
  draftRangeMax: String(initialSettings.rangeMax),
  isSaving: false,
  hasLoaded: true,
};

export const saveBackfireSettingsAction = createAsyncThunk(
  "backfire/saveBackfireSettings",
  async ({
    account,
    settings,
  }: {
    account: string;
    settings: BackfireSettings;
  }) => {
    const fixedAmount =
      settings.mode === BackfireMode.Range ? settings.rangeMin : 0;

    await setBackfire(account, settings.mode, fixedAmount);

    // Cache locally — on-chain backfireSettings mapping has no public getter.
    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        BACKFIRE_STORAGE_KEY,
        JSON.stringify(settings)
      );
    }

    return settings;
  }
);

const backfireSlice = createSlice({
  name: "backfire",
  initialState,
  reducers: {
    setDraftBackfireMode: (state, { payload }: PayloadAction<BackfireMode>) => {
      state.draftMode = payload;
    },
    setDraftBackfireRangeMin: (state, { payload }: PayloadAction<string>) => {
      state.draftRangeMin = payload;
    },
    setDraftBackfireRangeMax: (state, { payload }: PayloadAction<string>) => {
      state.draftRangeMax = payload;
    },
    resetBackfireDraft: (state) => {
      state.draftMode = state.settings.mode;
      state.draftRangeMin = String(state.settings.rangeMin);
      state.draftRangeMax = String(state.settings.rangeMax);
    },
    hydrateBackfireSettings: (
      state,
      { payload }: PayloadAction<BackfireSettings>
    ) => {
      const normalized = normalizeBackfireSettings(payload);
      state.settings = normalized;
      state.draftMode = normalized.mode;
      state.draftRangeMin = String(normalized.rangeMin);
      state.draftRangeMax = String(normalized.rangeMax);
      state.hasLoaded = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(saveBackfireSettingsAction.pending, (state) => {
      state.isSaving = true;
    });
    builder.addCase(saveBackfireSettingsAction.fulfilled, (state, { payload }) => {
      state.isSaving = false;
      state.settings = payload;
      state.draftMode = payload.mode;
      state.draftRangeMin = String(payload.rangeMin);
      state.draftRangeMax = String(payload.rangeMax);
    });
    builder.addCase(saveBackfireSettingsAction.rejected, (state) => {
      state.isSaving = false;
    });
  },
});

export const {
  setDraftBackfireMode,
  setDraftBackfireRangeMin,
  setDraftBackfireRangeMax,
  resetBackfireDraft,
  hydrateBackfireSettings,
} = backfireSlice.actions;

export default backfireSlice.reducer;
